import { CircleAlert } from "lucide-react";

import { useCurrentOrg } from "@/providers/auth/hooks";
import { AgentThreadErrorContent } from "@/shared/domain/agentThread/AgentThreadRecord";
import { OrganizationTier } from "@/shared/domain/orgs";
import { Banner } from "@/shared/foundation/primitives";

import styles from "./AssistantErrorContent.module.css";

// Matches the backend wire code set on AI-credit-cap refusals
// (roboto_service.worker.operations.ai_chat). Switching on it lets the web UI
// surface an upgrade affordance for the quota case without a schema change.
const AI_CREDIT_LIMIT_EXCEEDED_ERROR_CODE = "AI_CREDIT_LIMIT_EXCEEDED";
const PRICING_URL = "https://www.roboto.ai/pricing/";

interface AssistantErrorContentProps {
  error: AgentThreadErrorContent;
  /** Called by the Retry button; without it, no Retry is offered. */
  onRetry?: () => void;
  /** Why the last retry from this notice did not run. */
  retryRefusal?: string;
}

/**
 * An error in the conversation, such as a reply that failed or a turn the
 * server refused, shown as an error banner.
 */
export const AssistantErrorContent = ({
  error,
  onRetry,
  retryRefusal,
}: AssistantErrorContentProps) => {
  return (
    <div className={styles.indentedContainer}>
      <Banner
        variant="error"
        icon={<CircleAlert aria-hidden="true" />}
        description={
          <>
            <p className={styles.message}>{error.error_message}</p>
            {retryRefusal === undefined ? null : (
              <p className={styles.message}>{retryRefusal}</p>
            )}
            <UpgradeLink error={error} />
          </>
        }
        action={
          onRetry === undefined ? undefined : (
            <Banner.Action variant="secondary" onClick={onRetry}>
              Retry
            </Banner.Action>
          )
        }
      />
    </div>
  );
};

/**
 * A demo-request link to upgrade, for a free org at the AI-credit cap. The
 * backend sends the same error code for both tiers (only the message text
 * differs), and a premium org's message tells them to contact their admin, so
 * the link waits until the org is known to be free.
 */
function UpgradeLink({ error }: { error: AgentThreadErrorContent }) {
  const currentOrg = useCurrentOrg();
  if (
    error.error_code !== AI_CREDIT_LIMIT_EXCEEDED_ERROR_CODE ||
    currentOrg?.tier !== OrganizationTier.Free
  ) {
    return null;
  }
  return (
    <a
      className={styles.upgradeLink}
      href={PRICING_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      Request a demo to upgrade
    </a>
  );
}
